import CustomModal from "../../Common/CustomModal";
import AvatarCropModal from "../AvatarCropModal";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import { fetchMemberDirectoryStatus } from "../../../utils/FetchGhlUser/fetchGhlUser";
import { useAvatarCrop } from "../../../hooks/useAvatarCrop";
import { Gapper } from "../../ui/Gapper";
import { InfoText, ContentBox } from "../shared/practitionerForm.styles";
import {
  MODAL_TITLE,
  SAVE_BTN_TEXT,
  CANCEL_BTN_TEXT,
  DISCLAIMERS,
  DIRECTORY_STATUS_FIELD_ID,
} from "./ResourcesModal.constants";
import { isFormEmpty } from "../shared/practitionerForm.utils";
import { validateImageFile } from "../../../utils/avatarValidation";
import { useCountries } from "./hooks/useCountries";
import DirectoryStatusSection from "./components/DirectoryStatusSection";
import BioAndSpecialtiesSection from "../shared/components/BioAndSpecialtiesSection";
import WebsiteAndSocialsSection from "../shared/components/WebsiteAndSocialsSection";
import ContactInfoSection from "./components/ContactInfoSection";
import CoverPictureSection from "./components/CoverPictureSection";

const ResourcesModal = ({
  open,
  handleClose,
  formData,
  setFormData,
  handleSave,
  dataURItoBlob,
  setErrorState,
}) => {
  const { practitioner } = useSelector((state) => state.user);
  const [directoryStatus, setDirectoryStatus] = useState(false);
  const [coverPictureLoading, setCoverPictureLoading] = useState(false);
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down(900));
  const countries = useCountries();

  const avatarCrop = useAvatarCrop(
    (file, field) => dataURItoBlob(file, field),
    formData?.avatar,
    setErrorState
  );

  useEffect(() => {
    const fetchData = async () => {
      if (!practitioner?.iaihId) return;

      try {
        const response = await fetchMemberDirectoryStatus(practitioner.iaihId);
        if (response && !response.error) {
          const isApproved =
            response.contact?.customField?.find(
              (field) => field.id === DIRECTORY_STATUS_FIELD_ID
            )?.value === "approved";
          setDirectoryStatus(isApproved);
        } else if (response?.error) {
          console.error("GHL API Error:", response.error);
        }
      } catch (err) {
        console.error("Unexpected error:", err);
      }
    };
    fetchData();
  }, [practitioner]);

  useEffect(() => {
    if (formData?.coverPicture) setCoverPictureLoading(true);
  }, [formData?.coverPicture]);

  const updateField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCoverPictureSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!validateImageFile(file, setErrorState)) {
      e.target.value = "";
      return;
    }
    setCoverPictureLoading(true);
    try {
      await dataURItoBlob(file, "coverPicture");
    } catch {
      setCoverPictureLoading(false);
    }
    e.target.value = "";
  };

  return (
    <>
      <CustomModal
        open={open}
        handleClose={handleClose}
        title={MODAL_TITLE}
        width={isMediumScreen ? "98%" : "75%"}
        saveBtnText={SAVE_BTN_TEXT}
        cancelBtnText={CANCEL_BTN_TEXT}
        disableBtn={isFormEmpty(formData)}
        onSave={handleSave}
        onCancel={handleClose}
      >
        <Box>
          {DISCLAIMERS.map((text, index) => (
            <InfoText key={index}>{text}</InfoText>
          ))}
        </Box>
        <DirectoryStatusSection directoryStatus={directoryStatus} />
        <ContentBox>
          <Gapper gap={0}>
            <BioAndSpecialtiesSection
              submitData={formData}
              updateField={updateField}
              avatarSectionProps={{
                avatar: formData?.avatar,
                avatarLoading: avatarCrop.avatarLoading,
                onFileSelect: avatarCrop.handleAvatarFileSelect,
                onImageLoad: () => avatarCrop.setAvatarLoading(false),
                onImageError: () => avatarCrop.setAvatarLoading(false),
              }}
              coverPictureSection={
                <CoverPictureSection
                  coverPicture={formData?.coverPicture}
                  coverPictureLoading={coverPictureLoading}
                  onFileSelect={handleCoverPictureSelect}
                  onImageLoad={() => setCoverPictureLoading(false)}
                  onImageError={() => setCoverPictureLoading(false)}
                />
              }
            />
            <WebsiteAndSocialsSection
              submitData={formData}
              updateField={updateField}
            />
            <ContactInfoSection
              formData={formData}
              updateField={updateField}
              countries={countries}
            />
          </Gapper>
        </ContentBox>
      </CustomModal>
      <AvatarCropModal
        open={avatarCrop.avatarCropOpen}
        image={avatarCrop.avatarCropImage}
        onConfirm={avatarCrop.handleAvatarCropConfirm}
        onCancel={avatarCrop.avatarCropCancel}
      />
    </>
  );
};

export default ResourcesModal;
